import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { OtherRankInput, selValue } from "@/lib/ranks";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { SeaWages, EMPTY_WAGES, wagesTotal } from "@/components/SeaWages";
import { AddressBlock, EMPTY_ADDRESS, MobileInput, addressError, mobileError } from "@/components/SeaAddress";

export const SEA_EMPTY = {
  seafarer_name: "", rank: "", cdc_number: "", passport_number: "", contract_duration: "",
  joining_port: "", joining_date: "", nok_name: "", port_of_engagement: "", home_port_airport: "",
  currency: "USD", wages: EMPTY_WAGES, seafarer_address: EMPTY_ADDRESS, nok_address: EMPTY_ADDRESS,
  nok_mobile_code: "+91", nok_mobile_number: "",
};

const TEXT_KEYS = ["seafarer_name", "rank", "cdc_number", "passport_number", "contract_duration", "joining_port",
  "joining_date", "nok_name", "port_of_engagement", "home_port_airport"];

const DURATIONS = ["3 months", "4 months", "6 months", "9 months", "12 months"];

const TEXT_FIELDS = [
  ["seafarer_name", "Seafarer name", "e.g. Ravi Kumar"],
  ["cdc_number", "CDC number", "e.g. MUM123456"],
  ["passport_number", "Passport number", "e.g. Z1234567"],
  ["joining_port", "Joining port", "e.g. Singapore"],
  ["port_of_engagement", "Port of engagement", "e.g. Mumbai"],
  ["home_port_airport", "Home port / airport", "e.g. Mumbai (BOM)"],
];

function ReadOnly({ label, value, testid }) {
  return (
    <div className="space-y-1.5">
      <Label>{label} <span className="text-slate-400 font-normal">(auto)</span></Label>
      <Input value={value || ""} readOnly disabled className="bg-slate-50" data-testid={testid} />
    </div>
  );
}

function PickList({ label, value, options, onChange, testid, placeholder }) {
  const sel = selValue(value);
  const opts = sel && !options.includes(sel) ? [sel, ...options] : options;
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      <Select value={sel} onValueChange={onChange}>
        <SelectTrigger data-testid={testid}><SelectValue placeholder={placeholder} /></SelectTrigger>
        <SelectContent>{opts.map((o) => <SelectItem key={o} value={o} data-testid={`${testid}-option-${o}`}>{o}</SelectItem>)}</SelectContent>
      </Select>
      <OtherRankInput value={value} onChange={onChange} testid={`${testid}-other-input`} />
    </div>
  );
}

export function SeaFields({ form, setForm, ranks, vesselName, ownerName }) {
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const put = (k) => (v) => setForm({ ...form, [k]: v });
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="sea-fields">
      <ReadOnly label="Vessel name" value={vesselName} testid="sea-vessel-name-input" />
      <ReadOnly label="Owner name" value={ownerName} testid="sea-owner-name-input" />
      <PickList label="Rank" value={form.rank} options={ranks} onChange={put("rank")} testid="sea-rank-select" placeholder="Select rank" />
      <PickList label="Contract duration" value={form.contract_duration} options={DURATIONS} onChange={put("contract_duration")} testid="sea-duration-select" placeholder="Select duration" />
      <div className="space-y-1.5">
        <Label htmlFor="sea-joining-date">Joining date</Label>
        <Input id="sea-joining-date" type="date" value={form.joining_date} onChange={set("joining_date")} data-testid="sea-joining_date-input" />
      </div>
      {TEXT_FIELDS.map(([k, label, ph]) => (
        <div key={k} className="space-y-1.5">
          <Label htmlFor={`sea-${k}`}>{label}</Label>
          <Input id={`sea-${k}`} value={form[k]} placeholder={ph} onChange={set(k)} data-testid={`sea-${k}-input`} />
        </div>
      ))}
      <div className="sm:col-span-2">
        <SeaWages currency={form.currency} wages={form.wages} onCurrency={put("currency")} onWages={put("wages")} />
      </div>
      <AddressBlock title="Seafarer address" value={form.seafarer_address} onChange={put("seafarer_address")} prefix="sea-seafarer-address" />
      <div className="space-y-1.5">
        <Label htmlFor="sea-nok_name">Next of kin name</Label>
        <Input id="sea-nok_name" value={form.nok_name} placeholder="e.g. Sita Kumar" onChange={set("nok_name")} data-testid="sea-nok_name-input" />
      </div>
      <MobileInput label="Next of kin mobile" code={form.nok_mobile_code} number={form.nok_mobile_number} prefix="sea-nok-mobile"
        onChange={(code, number) => setForm({ ...form, nok_mobile_code: code, nok_mobile_number: number })} />
      <AddressBlock title="Next of kin address" value={form.nok_address} onChange={put("nok_address")} prefix="sea-nok-address" />
    </div>
  );
}

export function validateSea(form) {
  const missing = TEXT_KEYS.filter((k) => !String(form[k] || "").trim());
  if (missing.length) return `Please fill in all fields (${missing.length} missing)`;
  if (Object.values(form.wages).some((v) => v !== "" && (Number.isNaN(Number(v)) || Number(v) < 0))) return "Wage amounts must be positive numbers";
  if (wagesTotal(form.wages) <= 0) return "Enter at least one wage component";
  return addressError(form.seafarer_address, "Seafarer address")
    || addressError(form.nok_address, "Next of kin address")
    || (mobileError(form.nok_mobile_code, form.nok_mobile_number) && `Next of kin mobile: ${mobileError(form.nok_mobile_code, form.nok_mobile_number)}`);
}

export function toSeaPayload(form) {
  const { nok_mobile_code, nok_mobile_number, wages, ...rest } = form;
  return {
    ...rest,
    wages: Object.fromEntries(Object.entries(wages).filter(([, v]) => v !== "").map(([k, v]) => [k, Number(v)])),
    nok_mobile: `${nok_mobile_code.trim()} ${nok_mobile_number}`,
  };
}

export function fromContract(c) {
  const [code, number] = (c.nok_mobile || "+91 ").split(" ");
  return {
    ...Object.fromEntries(TEXT_KEYS.map((k) => [k, c[k] || ""])),
    currency: c.currency || "USD",
    wages: { ...EMPTY_WAGES, ...Object.fromEntries(Object.entries(c.wages || {}).map(([k, v]) => [k, String(v)])) },
    seafarer_address: { ...EMPTY_ADDRESS, ...(typeof c.seafarer_address === "object" ? c.seafarer_address : {}) },
    nok_address: { ...EMPTY_ADDRESS, ...(typeof c.nok_address === "object" ? c.nok_address : {}) },
    nok_mobile_code: code || "+91",
    nok_mobile_number: number || "",
  };
}

export async function downloadContractPdf(api, c) {
  const { data } = await api.get(`/sea-contracts/${c.id}/pdf`, { responseType: "blob" });
  const url = URL.createObjectURL(data);
  const a = document.createElement("a");
  a.href = url;
  a.download = `SEA_${(c.seafarer_name || "contract").replace(/\W+/g, "_")}_${(c.vessel_name || "").replace(/\W+/g, "_")}.pdf`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}
